import { FileAttachment } from '../types/chat';

/**
 * Converts a File object to base64 string
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Converts an array of File objects to FileAttachment objects with base64 data
 */
export const filesToAttachments = async (files: File[]): Promise<FileAttachment[]> => {
  const attachments: FileAttachment[] = [];

  for (const file of files) {
    try {
      const base64Data = await fileToBase64(file);
      attachments.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        data: base64Data
      });
    } catch (error) {
      console.error(`Failed to convert file ${file.name} to base64:`, error);
      throw new Error(`Failed to process file: ${file.name}`);
    }
  }

  return attachments;
};

/**
 * Validates file size and type
 */
export const validateFile = (file: File): { valid: boolean; error?: string } => {
  const allowedTypes = ['application/pdf', 'text/plain', 'image/png', 'image/jpeg', 'image/jpg'];
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type: ${file.name}. Only PDF, TXT, PNG, JPG, and JPEG files are allowed.`
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File too large: ${file.name}. Maximum size is 10MB.`
    };
  }

  return { valid: true };
};

/**
 * Formats file size in human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Math.round(bytes / Math.pow(k, i))}${sizes[i]}`;
};
